import { useMemo } from "react"

import { Table } from "antd"
import cls from "classname"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"

import EmptyComponent from "../../../../Components/Empty"
import { getDashboardProgressRowProps } from "../../../DashboardPage/components/Table/columns"
import ChangeBadge from "../../components/Badges/ChangeBadge"
import StatusBadge from "../../components/Badges/StatusBadge"
import ChannelValue from "../../components/cells/ChannelValue"
import { numberCell } from "../../components/cells/numberCell"
import ProgramTag from "../../components/cells/ProgramTag"
import KpiStrip from "../../components/KpiStrip/KpiStrip"
import { listPagination, listPaginationClass } from "../../components/ListPagination/listPagination"
import MetricCard from "../../components/MetricCard/MetricCard"
import { Panel, PanelRow } from "../../components/Panel/Panel"
import { useCardData, useListData } from "../../hooks/useCardData"
import { getPointChange, GOOD_DIRECTION } from "../../utils/change"
import { formatCount, formatShare, toShare } from "../../utils/formats"

import rowLines from "../../components/RowLines/rowLines.module.less"

// The card's Tooltip terms, verbatim from the spec (§4 Unbillable channels).
const INFO = [
  {
    name: "Unbillable share",
    formula:
      "Lookups on a channel other than banking app, call center, Mastercom (empty included) ÷ all lookups × 100"
  },
  { name: "Change", formula: "Change in percentage points against the comparison period" },
  {
    name: "Count by value",
    formula: "Lookups in the period with this channel value in this program"
  },
  {
    name: "First / last seen",
    formula: "Earliest / latest date the value arrived in the program, over all history"
  },
  { name: "New value", formula: "A value whose first-seen date falls in the selected period" }
]

// "Sep 1, 2026" (DAA-4-15).
const formatSeenDate = (isoDate) => dayjs(isoDate).format("MMM D, YYYY")

// A value's share of all lookups, to two decimals (DAA-4-15).
const formatValueShare = (value) => formatShare(value, 2)

// Unbillable channels (DAA-4-13 … DAA-4-20): what part of lookups came through channels nobody pays
// for, and whether providers started sending new channel values — the KPIs, the values first seen in
// the period and every value of the period, each list a page at a time (CHA1-4558). No copy states a
// price (DAA-4-20). Channels and the merchant tag filters do not reach it (DAA-2-27).
const UnbillableChannelsCard = () => {
  const { t } = useTranslation()
  const { data, loading } = useCardData("unbillableChannels")
  const newValues = useListData("unbillableNewValues")
  const allValues = useListData("unbillableAllValues")
  const kpis = data?.kpis
  const comparison = data?.comparison

  const tiles = useMemo(
    () => [
      {
        key: "unbillableShare",
        label: t("Unbillable share"),
        value: kpis?.unbillable_share,
        decimals: 1,
        suffix: "%",
        // A change badge only while comparing (DAA-2-22); a fall is good.
        badge: comparison ? (
          <ChangeBadge
            change={getPointChange(
              kpis?.unbillable_share,
              comparison.unbillable_share,
              GOOD_DIRECTION.DOWN
            )}
          />
        ) : null,
        secondary: comparison
          ? `vs ${formatShare(comparison.unbillable_share)} ${t("prev. period")}`
          : null,
        note: kpis
          ? `${formatCount(kpis.unbillable)} ${t("of")} ${formatCount(kpis.lookups)} ${t("lookups")}`
          : null
      },
      {
        key: "newValues",
        label: t("New values in period"),
        value: kpis?.new_values,
        // A status, not a change: it stays with Do not compare (DAA-2-22).
        badge: kpis ? <StatusBadge status={kpis.new_values > 0 ? "review" : "none"} /> : null,
        secondary: t("never seen before in this program")
      }
    ],
    [kpis, comparison, t]
  )

  const newRows = newValues.data?.results ?? []
  // `percentage` sets the share line along a row's bottom (All values only). It is relative to the
  // largest value of all pages, the first one, as rows arrive sorted by lookups (DAA-4-19).
  const allRows = useMemo(
    () =>
      (allValues.data?.results ?? []).map((row) => ({
        ...row,
        percentage: toShare(row.lookups, allValues.data.largest_lookups)
      })),
    [allValues.data]
  )

  const columns = useMemo(
    () => [
      {
        title: t("Value"),
        dataIndex: "channel",
        render: (value) => <ChannelValue value={value} />
      },
      {
        title: t("Program"),
        dataIndex: "program",
        width: 72,
        render: (program) => <ProgramTag program={program} />
      },
      {
        title: t("Lookups"),
        dataIndex: "lookups",
        align: "right",
        width: 72,
        render: numberCell(formatCount)
      },
      {
        title: t("Share"),
        dataIndex: "share",
        align: "right",
        width: 72,
        render: numberCell(formatValueShare)
      },
      {
        title: t("First seen"),
        dataIndex: "first_seen",
        align: "right",
        width: 104,
        render: formatSeenDate
      },
      {
        title: t("Last seen"),
        dataIndex: "last_seen",
        align: "right",
        width: 104,
        render: formatSeenDate
      }
    ],
    [t]
  )

  // Both tables share their columns, each with its own pages. Below 520px — half a card on a narrow
  // screen, or a phone — a table with rows scrolls sideways instead of squeezing the Value column out.
  // A page change spins the table alone; under the card's own spinner it stays still.
  const tableProps = (list, rows) => ({
    className: cls("chart-table", rowLines.rowLines, listPaginationClass),
    // A value is one channel in one program (DAA-4-14).
    rowKey: (row) => `${row.program}-${row.channel}`,
    columns,
    dataSource: rows,
    loading: list.loading && !loading,
    pagination: listPagination(list, t),
    tableLayout: "fixed",
    showHeader: rows.length > 0,
    scroll: rows.length > 0 ? { x: 520 } : undefined
  })

  return (
    <MetricCard
      title={t("Unbillable channels")}
      info={INFO}
      notAffectedBy={t("Not affected by: Channels, User email, User full name, Business account")}
      loading={loading}
    >
      <KpiStrip tiles={tiles} />
      {/* New values beside All values, under the divider of the two KPIs (CHA1-4558, DAA-1-21). */}
      <PanelRow>
        <Panel title={t("New values")} span={6}>
          {newValues.data && (
            <Table
              {...tableProps(newValues, newRows)}
              locale={{ emptyText: <EmptyComponent type="no_new_values" /> }}
            />
          )}
        </Panel>
        <Panel title={t("All values")} tag={t("sorted by lookups")} span={6}>
          {allValues.data && (
            <Table
              {...tableProps(allValues, allRows)}
              onRow={getDashboardProgressRowProps}
              locale={{ emptyText: <EmptyComponent type="no_data_available" /> }}
            />
          )}
        </Panel>
      </PanelRow>
    </MetricCard>
  )
}

export default UnbillableChannelsCard
